import { PIN } from '../rcu/catalog/security';

const PIN_HASH = {
  /** 32-bit FNV-1a parameters. */
  OFFSET_BASIS: 0x811c9dc5,
  PRIME: 0x01000193,
  SALT_BYTES: 8,
  BYTE_RANGE: 256,
  HEX_RADIX: 16,
} as const;

/**
 * Salted FNV-1a hash of a PIN.
 *
 * This is obfuscation, not cryptography. The gate keeps a curious guest out of
 * a device-local configuration; anyone holding the tablet can clear storage.
 * The salt defeats precomputed tables over the 10,000 possible PINs.
 */
const hashPin = (pin: string, salt: string): string => {
  const input = `${salt}:${pin}`;
  let hash: number = PIN_HASH.OFFSET_BASIS;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, PIN_HASH.PRIME);
  }
  return (hash >>> 0).toString(PIN_HASH.HEX_RADIX);
};

const newSalt = (): string => {
  const bytes = new Uint8Array(PIN_HASH.SALT_BYTES);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * PIN_HASH.BYTE_RANGE);
    }
  }
  return Array.from(bytes, (byte) => byte.toString(PIN_HASH.HEX_RADIX).padStart(2, '0')).join('');
};

export interface StoredPin {
  readonly salt: string;
  readonly hash: string;
}

export const createPin = (pin: string): StoredPin => {
  const salt = newSalt();
  return { salt, hash: hashPin(pin, salt) };
};

export const verifyPin = (pin: string, stored: StoredPin): boolean =>
  hashPin(pin, stored.salt) === stored.hash;

/** Exactly `PIN.LENGTH` digits. Enforced before hashing, not after. */
export const isValidPinFormat = (pin: string): boolean =>
  new RegExp(`^[0-9]{${PIN.LENGTH}}$`).test(pin);
